import React from 'react';
import _merge from 'lodash/merge';
import { getContext, useCombinedRefs, isDarkTheme, isAnimationDisabled } from 'common';
import { getElevationStyle, ElevationProps } from 'components/Elevation';
import Styled from './styles';

interface ExpansionPanelInterface {
  mountContent?: any;
  expanded?: boolean;
  expandedHeight?: number;
  hoverEffect?: boolean;
  focusEffect?: boolean;
  stickyHeader?: boolean;
  rtl?: boolean;
  disabled?: boolean;
  selected?: boolean;
  disableAnimation?: boolean;
}
const ExpansionPanelContext = React.createContext<ExpansionPanelInterface | null>({});

export interface ExpansionPanelProps extends React.ComponentPropsWithRef<'div'> {
  /** ExpansionPanel children. Consists of <ExpansionPanelHeader/>, <ExpansionPanelBody/> */
  children: React.ReactNode;
  /** If `true`, the component will be disabled. */
  disabled?: boolean;
  /** If `true`, display the panel as selected. */
  selected?: boolean;
  /** If `true, the <ExpansionPanelBody/> portion of the ExpansionPanel is expanded. */
  expanded?: boolean;
  /** If `true', show a gap between expanded panels. */
  expansionGap?: boolean;
  /** If `true`, mounts expanded content. Disabling this will only mount content when the panel is expanded (useful for performance considerations). */
  mountContent?: boolean;
  /** Height is auto-calculated but can be overridden entirely with this property (in pixels) */
  expandedHeight?: number;
  /** If `true`, the header will remain 'sticky' (positioned at the top) as content is scrolled. */
  stickyHeader?: boolean;
  /** if `true`, the hover effects on the panel header are displayed. */
  hoverEffect?: boolean;
  /** if `true`, the focus effects on the panel header are displayed. */
  focusEffect?: boolean;
  /** If `false` turns off default styles (background color, shadow, and border styles) on the panel. */
  defaultStyles?: boolean;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Override the `Elevation` properties. See `Elevation` component for details. */
  elevationProps?: Partial<ElevationProps>;
  /** @ignore */
  style?: React.CSSProperties
}

/** ExpansionPanel is used to show more data when expanded and hide it when collapsed.
 * All additional props will be forwarded to the root element.
*/
const ExpansionPanel = React.forwardRef<HTMLElement, ExpansionPanelProps>((props: ExpansionPanelProps, ref) =>
  getContext(({ RTL, theme, disableAnimation }) => {
    const containerRef = React.useRef(null);
    const combinedRef = useCombinedRefs(ref, containerRef);
    const {
      children,
      expanded,
      expansionGap,
      expandedHeight,
      mountContent,
      stickyHeader,
      hoverEffect,
      focusEffect,
      defaultStyles,
      elevationProps,
      style: propsStyle,
      disableAnimation: propsdisableAnimation,
      theme: userTheme,
      disabled,
      selected,
      ...otherProps
    } = props;
    const resolvedTheme = _merge({}, theme.EXPANSION_PANEL, userTheme);
    const darkTheme = isDarkTheme(theme.NAME);
    const resolvedDisableAnimation = isAnimationDisabled(propsdisableAnimation, disableAnimation);
    const ELEVATION_STYLES = defaultStyles ? getElevationStyle(elevationProps) : {};
    const mergedElevationStyle = darkTheme ? { ...ELEVATION_STYLES, backgroundColor: resolvedTheme.background } : { ...ELEVATION_STYLES };
    return (
      <Styled.ExpansionPanel
        ref={combinedRef}
        style={propsStyle}
        $theme={resolvedTheme}
        $expanded={expanded}
        $rtl={RTL}
        $expansionGap={expansionGap}
        $mountContent={mountContent}
        $stickyHeader={stickyHeader}
        $defaultStyles={defaultStyles}
        $hoverEffect={hoverEffect}
        $focusEffect={focusEffect}
        $elevationStyles={mergedElevationStyle}
        className="ccfk-expansion-panel-tabable"
        $disableAnimation={resolvedDisableAnimation}
        {...otherProps}
      >
        <ExpansionPanelContext.Provider
          value={{
            mountContent,
            expanded,
            expandedHeight,
            hoverEffect,
            focusEffect,
            stickyHeader,
            rtl: RTL,
            disabled,
            selected,
            disableAnimation: resolvedDisableAnimation,
          }}
        >
          {children}
        </ExpansionPanelContext.Provider>
      </Styled.ExpansionPanel>
    );
  }));

ExpansionPanel.defaultProps = {
  expanded: false,
  disabled: false,
  selected: false,
  expansionGap: false,
  mountContent: true,
  expandedHeight: undefined,
  stickyHeader: false,
  hoverEffect: true,
  focusEffect: true,
  defaultStyles: true,
  disableAnimation: undefined,
  theme: {},
  elevationProps: {
    elevationIndex: 2,
    dark: false,
  },
  style: {}
};
ExpansionPanel.displayName = 'ExpansionPanel';

/* Exports */
export { ExpansionPanel as default, ExpansionPanelContext };
